import { FC, useCallback, useMemo } from 'react';
import { Dropdown, DropdownSkeleton } from '@carbon/react';

import type { TypeCurveScenarioOption } from 'types/type-curve-scenario-options';

import { useTypeCurveScenarioOptions } from './use-type-curve-scenario-options';
import styles from './type-curve-scenario-picker.module.scss';

/** Props for {@link TypeCurveScenarioPicker}. */
export interface TypeCurveScenarioPickerProps {
  /** Currently-selected scenario id, or null when nothing is chosen yet. */
  value: number | null;
  /** Fired with the newly-chosen scenario id. Never fired with null. */
  onChange: (scenarioId: number) => void;
  /** Disables the control — e.g. while a switch is materializing. */
  disabled?: boolean;
}

const EMPTY_LABEL = 'Select a scenario';

/**
 * Dropdown over the org's promoted Type Curve Scenarios.
 *
 * The server owns the population rule — `GET /type_curve_scenarios?promoted=true` returns only
 * scenarios carrying current-promoted content, already name-ordered — so this component never
 * filters. An empty list is not an error; the dropdown simply renders with nothing to pick,
 * which is the honest state when nothing has been promoted.
 *
 * The package wizard's scenario picker. A scenario is required, so an empty value reads as
 * invalid.
 *
 * @param {TypeCurveScenarioPickerProps} props - Selection, change handler and disabled flag.
 * @return {JSX.Element} The dropdown, or a skeleton while the options load.
 */
export const TypeCurveScenarioPicker: FC<TypeCurveScenarioPickerProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const { options, loading } = useTypeCurveScenarioOptions();

  // Carbon models "nothing selected" as undefined, not null.
  const selected = useMemo(
    () => options.find((option) => option.id === value),
    [options, value]
  );

  const handleChange = useCallback(
    ({ selectedItem }: { selectedItem?: TypeCurveScenarioOption | null }) => {
      // Carbon emits null when the control is cleared. There is no "no scenario"
      // state to move to once one is chosen, so a null selection is ignored.
      if (selectedItem) onChange(selectedItem.id);
    },
    [onChange]
  );

  if (loading && options.length === 0) {
    return (
      <div className={styles.picker}>
        <DropdownSkeleton className={styles.dropdown} />
      </div>
    );
  }

  return (
    <div className={styles.picker}>
      <Dropdown<TypeCurveScenarioOption>
        id="type-curve-scenario-picker"
        className={styles.dropdown}
        size="sm"
        type="inline"
        titleText="Type Curve Scenario"
        label={EMPTY_LABEL}
        items={options}
        itemToString={(item) => item?.name ?? ''}
        selectedItem={selected}
        onChange={handleChange}
        disabled={disabled}
        // Carbon's own invalid text, not a sibling span, so it's linked to the control for
        // screen readers.
        invalid={value === null}
        invalidText="Required"
      />
    </div>
  );
};
